import { motion } from "framer-motion";
import { Cpu, Trophy, Sparkles, Mic, Bot, Users } from "lucide-react";
import { ZONES, AUDIENCE, PDF_BG } from "../data/content";
import SectionBg from "./SectionBg";

const ZONE_ICONS = [Users, Trophy, Sparkles, Mic, Bot, Cpu];

export function Zones() {
    return (
        <section
            id="zones"
            className="relative py-24 md:py-40 overflow-hidden"
            data-testid="zones-section"
        >
            <SectionBg src={PDF_BG.zones} opacity={0.6} />
            <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative">
                <div className="grid grid-cols-12 gap-6 md:gap-12 mb-12 md:mb-16">
                    <div className="col-span-12 md:col-span-4 text-xs uppercase tracking-[0.3em] text-[#FF6B35] font-mono">
                        The Festival Floor
                    </div>
                    <h2 className="col-span-12 md:col-span-8 min-w-0 max-w-full font-display font-bold text-[1.62rem] leading-[1.14] tracking-normal break-words [overflow-wrap:anywhere] sm:text-5xl sm:leading-[1.05] sm:tracking-tight lg:text-6xl">
                        Six worlds. <span className="text-stroke-orange">One weekend.</span>
                    </h2>
                </div>

                <div className="grid grid-cols-12 gap-4 md:gap-6">
                    {ZONES.map((z, i) => {
                        const Icon = ZONE_ICONS[i] || Sparkles;
                        const span = i === 0 || i === 3 ? "col-span-12 md:col-span-7" : "col-span-12 md:col-span-5";
                        return (
                            <motion.div
                                key={z.name}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ delay: i * 0.07, duration: 0.7 }}
                                whileHover={{ y: -6 }}
                                className={`${span} group relative rounded-3xl border border-white/10 bg-[#141414] p-5 sm:p-8 md:p-10 overflow-hidden hover:border-[#FF6B35]/60 transition-all duration-500`}
                                data-testid={`zone-card-${i}`}
                            >
                                <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#9D4CDD]/0 group-hover:bg-[#9D4CDD]/20 blur-3xl transition-all duration-700" />
                                <div className="relative flex flex-col h-full min-h-[180px] sm:min-h-[200px]">
                                    <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-[#FF6B35]/10 border border-[#FF6B35]/20 flex items-center justify-center text-[#FF6B35] group-hover:bg-[#FF6B35] group-hover:text-black transition-all duration-500">
                                        <Icon size={22} />
                                    </div>
                                    <h3 className="mt-5 sm:mt-6 max-w-full font-display text-[1.45rem] leading-tight font-bold tracking-normal break-words [overflow-wrap:anywhere] md:text-3xl md:tracking-tight">{z.name}</h3>
                                    <p className="mt-3 text-sm sm:text-base text-zinc-400 leading-relaxed [overflow-wrap:anywhere]">{z.desc}</p>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

export function TargetAudience() {
    return (
        <section
            id="audience"
            className="relative py-24 md:py-40 overflow-hidden"
            data-testid="audience-section"
        >
            <SectionBg src={PDF_BG.audience} opacity={0.6} />
            <div className="pointer-events-none absolute top-0 left-0 w-[500px] h-[500px] rounded-full bg-[#FF6B35]/10 blur-[160px]" />
            <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative">
                <div className="grid grid-cols-12 gap-6 md:gap-12 mb-12 md:mb-16">
                    <div className="col-span-12 md:col-span-4 text-xs uppercase tracking-[0.3em] text-[#FF6B35] font-mono">
                        Who's In The Room
                    </div>
                    <h2 className="col-span-12 md:col-span-8 min-w-0 max-w-full font-display font-bold text-[1.62rem] leading-[1.14] tracking-normal break-words [overflow-wrap:anywhere] sm:text-5xl sm:leading-[1.05] sm:tracking-tight lg:text-6xl">
                        Built for everyone who <span className="gradient-text">moves culture</span>.
                    </h2>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                    {AUDIENCE.map((a, i) => (
                        <motion.div
                            key={a}
                            initial={{ opacity: 0, y: 20, scale: 0.95 }}
                            whileInView={{ opacity: 1, y: 0, scale: 1 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ delay: i * 0.04 }}
                            whileHover={{ y: -4, scale: 1.02 }}
                            className="group flex items-center gap-3 p-4 sm:p-5 rounded-2xl border border-white/10 bg-[#141414] hover:border-[#FF6B35]/60 hover:bg-[#FF6B35]/5 transition-all cursor-default"
                            data-testid={`audience-${i}`}
                        >
                            <span className="text-[#FF6B35] font-mono text-xs">
                                0{(i + 1).toString().padStart(2, "0")}
                            </span>
                            <span className="min-w-0 text-sm md:text-base text-zinc-200 group-hover:text-white [overflow-wrap:anywhere]">{a}</span>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}

export function Speakers() {
    return null;
}
